import { Payment, Payments_Succeed, PaymentsSort } from '@web/gql/graphql'
import { StrictTypedTypePolicies } from '@web/libs/apollo/typePolicies.helper'
import unionBy from '@web/libs/lodash/unionBy'

//https://www.apollographql.com/docs/react/caching/cache-field-behavior/#the-merge-function
type ReviewTargetStatus = 'all' | 'review_target'
export type PaymentsCacheMap = Record<ReviewTargetStatus, Payment[]>

/**
 * 리뷰 대상 여부로 payments 를 그룹화하여 캐싱
 * - 'all' : 모든 payments 캐싱
 * - 'review_target': 리뷰 대상 payments 캐싱
 */
export const paymentsCachePolicy: StrictTypedTypePolicies['Payments_Succeed'] = {
  fields: {
    payments: {
      read: (
        existingCacheMap: PaymentsCacheMap = {} as PaymentsCacheMap,
        { variables },
      ): Payments_Succeed['payments'] => {
        const isReviewTargetFilter = variables?.input?.isReviewTargetFilter ?? true
        if (isReviewTargetFilter) return existingCacheMap['review_target'] ?? []

        return existingCacheMap['all'] ?? []
      },
      merge: (
        existingCacheMap: PaymentsCacheMap = {} as PaymentsCacheMap,
        incomingPayments: Payment[] = [],
        { variables, readField },
      ): PaymentsCacheMap => {
        const cacheMap = Object.assign({}, existingCacheMap)

        const isReviewTarget: keyof Payment = 'isReviewTarget'
        const isCanceled: keyof Payment = 'isCanceled'
        const visitDateTime: keyof Payment = 'visitDateTime'
        const ref = '__ref' as keyof Payment

        const sortByVisitDateTimeDesc = (curr: Payment, next: Payment) => {
          if (!readField(visitDateTime, curr) || !readField(visitDateTime, next)) return 1
          return (readField(visitDateTime, curr) as string) > (readField(visitDateTime, next) as string) ? -1 : 1
        }

        const cachePayments = (key: ReviewTargetStatus, payments: Payment[]) => {
          let mergedPayments = unionBy(payments, cacheMap[key] ?? [], ref)

          // 리뷰대상여부 필터링
          if (key === 'review_target') {
            mergedPayments = mergedPayments.filter(payment => readField(isReviewTarget, payment))
          }

          // 갱신내역을 조회하는 경우 취소내역 필터링
          const isUpdatedPayments = variables?.input?.sort === PaymentsSort.UpdatedDatetimeAsc
          if (isUpdatedPayments) {
            mergedPayments = mergedPayments.filter(payment => !readField(isCanceled, payment))
          }

          // 결제일시 최신순 정렬
          cacheMap[key] = mergedPayments.sort(sortByVisitDateTimeDesc)
        }

        cachePayments('all', incomingPayments)
        cachePayments('review_target', incomingPayments)

        return cacheMap
      },
    },
  },
}
